import {
  AbstractMesh,
  AnimationGroup,
  Bone,
  Color3,
  Debug,
  Mesh,
  MorphTarget,
  PBRMaterial,
  Scene,
  SceneLoader,
  Skeleton,
  Vector3,
} from "babylonjs";
import { random, sample } from "lodash";
import { getMorphTargetIndex, playMorphTargetAnim, validateMorphTargetAction, getAvailableMorphTargets } from "./utils";
// Enable GLTF/GLB loader (side-effects)
import "@babylonjs/loaders/glTF";
import "babylonjs-loaders";
import { ModelConfig } from "../types";

type MyMesh = AbstractMesh | Mesh;

const cdnModelUrl = process.env.REACT_AWS_CDN;

export class Humanoid {
  name: string = "";
  skeleton: Skeleton | null = null;
  intervalId: number = 0;
  mainMesh: AbstractMesh | Mesh | null = null;
  meshes: MyMesh[] = [];
  trackingBone: Bone | null = null;
  ellipsoid: any = null; // FOR DEBUGGING: show the ellipsoid used for the cc.
  loaded = false;
  currentAnimationName: string = "";
  skeletonViewer: any = {};
  faceMesh: Mesh | null = null;
  blinkingInfluence: number = 0;
  isBlinkingLeftEye = false;
  isBlinkingRightEye = false;
  isTalking = false;
  currentAnimGroup: AnimationGroup | null = null;
  currentAnimName: string = "";
  animationGroups: AnimationGroup[] = [];
  blinkTimeoutId: number | null = null;

  // New properties for enhanced facial expressions
  isPerformingEyeAction = false;
  lastEyeActionTime: number = 0;
  currentMouthAction: string | null = null;
  eyeActionTimeouts: Map<string, number> = new Map();

  // Timeout tracking for cleanup
  idleCycleTimeoutId: any = null;
  eyeActionTimeoutId: any = null;

  // Cooldown tracking for smooth transitions
  private lastTalkingEndTime: number = 0;
  private readonly TALKING_END_COOLDOWN = 1000; // 1 second cooldown after talking stops

  // Safety mechanism to prevent infinite recursion
  private animationCallCount: number = 0;
  private readonly MAX_ANIMATION_CALLS = 1000; // Prevent infinite loops

  constructor(
    private modelName: any,
    public scene: Scene,
    private modelConfig: ModelConfig,
    private callback: () => void,
    private activate = true,
    private DEBUG = false
  ) {
    this.intervalId = window.setInterval(this.afterImport.bind(this), 300);

    // SceneLoader.ImportMeshAsync("", "/", modelDataURL, this.scene, null).then((res) => {

    // console.log('Loading model:', this.modelName + '.glb'); 

    SceneLoader.ImportMesh(
      "",
      cdnModelUrl + "/models/",
      this.modelName + ".glb",
      scene,
      (meshes, particleSystems, skeletons, animationGroups) => {
        // console.log('Model loaded successfully!'); // Debug log
        // console.log('Meshes:', meshes);
        // console.log('Skeletons:', skeletons);

        this.skeleton = skeletons[0];
        this.meshes = meshes;
        this.mainMesh = meshes[0];
        this.setFaceMesh();
        this.animationGroups = animationGroups;

        // Stop any default animations that might have started automatically
        // TODO: Uncomment this when we have a way to stop the default animations
        // this.stopAllAnimationGroups();

        for (const mesh of meshes) {
          mesh.position.addInPlace(this.modelConfig.positionOffset);
          mesh.alwaysSelectAsActiveMesh = true;

          // Only show when all meshes are rendered. This is an alternative to scene.executeWhenReady
          // HACK! Way of hiding the mesh that actually renders the mesh (doing .isVisible = false will not render the mesh)
          // mesh.position.z = 1000;
          // (mesh as Mesh).onAfterRenderObservable.addOnce((renderedMesh) => {
          //   this.renderedMeshes.add(renderedMesh.name);
          // });
        }

        this.mainMesh.skeleton = this.skeleton;

        // Improves performance when we're picking stuff with rays. Also, this doesn't work (meshes deformed with rigs are on GPU, picking is done with CPU).
        // In order to pick the mesh, we'll use the this.colliders boxes.
        this.mainMesh.isPickable = false;
        // XXX: If this is false, how come cc collides the mesh with the ground??? BECAUSE it sets an ellipsoid, and calls moveWithCollision().
        // ... so no need for checkCollisions to be true.
        this.mainMesh.checkCollisions = false;

        this.mainMesh.receiveShadows = false;

        if (this.modelConfig.initialAnimation) {
          // For initial animation, use idle animation playback speed if available
          const idleAnim = this.getRandomIdleAnim();
          const playbackSpeed = idleAnim ? idleAnim.playbackSpeed : 1.0;
          this.setAnim(this.modelConfig.initialAnimation, false, undefined, playbackSpeed);
        }

        // Rotate the character, so the z axis is pointing forward.
        this.mainMesh.rotation.y = Math.PI;

        if (!this.activate) this.mainMesh.setEnabled(false);

        // Handle mesh shadows, backface culling, etc.
        // this.childMeshes = childMeshes
        // this.processChildMeshes()

        if (this.DEBUG) this.DEBUGSTUFF();
      },
      (progress) => {
        // Loading progress
      },
      (scene, errorMessage, errorObj) => {
        console.error("Error loading mesh:", errorMessage, "\nerrorObj:", errorObj);
        // console.error("Model name:", this.modelName);
        // console.error("Full path:", "/" + this.modelName + ".glb");
      },
      ".glb"
    );
  }

  hide() {
    this.mainMesh?.setEnabled(false);
  }

  show() {
    this.mainMesh?.setEnabled(true);
  }

  // XXX: This is probably not necessary... but okay.
  playAnimation(animationName: string, loop: boolean) {
    // let animationRange = this.skeleton.getAnimationRange(animationName);
    // this.scene.beginAnimation(this.skeleton, from, to, loop, 1);

    this.skeleton?.beginAnimation(animationName, loop);
  }

  // Call this function OUTSIDE of the ImportMesh callback. Otherwise console errors will just say "error on ImportMesh".
  afterImport() {
    if (
      this.skeleton != null
      //this.renderedMeshes.size === this.meshes.length
    ) {
      window.clearInterval(this.intervalId);

      //this.meshes.map((mesh) => (mesh.position.z = 0));

      // This removes the specular thing from THE SKIN MATERIALS
      /*
      if ("subMaterials" in this.mainMesh && this.mainMesh.subMaterials !== undefined) {
        for (let m of this.mainMesh.subMaterials) {
          this.processMaterial(m);
        }
      } else {
        this.processMaterial(this.mainMesh.material);
      }
      */

      this.loaded = true;

      // NOTE: The following enableBlending is for regular bone animations, not animation groups.
      // XXX: WARNING: Without this, you'll get the t-poses in between anim change.
      // https://www.html5gamedevs.com/topic/32712-animation-interpolation-and-blendingspeed/
      // this.skeleton.enableBlending(0.1);

      this.callback();

      this.blinkTimeoutId = window.setTimeout(() => this.blink(), 2000);

      // Set up proper idle behavior after model is loaded
      this.setupIdleBehavior();

      // Validate morph target configuration for debugging
      if (this.DEBUG) {
        this.validateMorphTargetConfig();
      }

      // Debug the talking animations.
      // this.talkAnimationStart();
    }
  }

  processMaterial(material: PBRMaterial) {
    material.specularIntensity = 0;
    material.metallicF0Factor = 0;
    // 0.4 is a truly magical value. It removes the weird black shapes in the characters.
    material.roughness = 0.4;

    material.backFaceCulling = false;
  }

  setDefaultEllipsoid() {
    this.updateEllipsoid(new Vector3(0.15, 1, 0.15), new Vector3(0, 1, 0.1));
  }

  updateEllipsoid(diameter: Vector3, offset: Vector3) {
    if (this.mainMesh) {
      this.mainMesh.ellipsoid = diameter;
      this.mainMesh.ellipsoidOffset = offset;
      //this.mainMesh.refreshBoundingInfo();
    }
  }

  copyPropsToOtherHumanoid(otherHumanoid: Humanoid) {
    if (otherHumanoid.mainMesh && this.mainMesh) {
      otherHumanoid.mainMesh.position = this.mainMesh.position.clone();
    }
  }

  // Check if the avatar is ready for lip sync
  isReadyForLipSync(): boolean {
    if (!this.loaded || this.faceMesh === null) {
      return false;
    }

    // Check for new multiple mouth movements
    if (this.modelConfig.morphTargets.mouthMovements && this.modelConfig.morphTargets.mouthMovements.length > 0) {
      // Verify at least one morph target exists
      const firstAction = this.modelConfig.morphTargets.mouthMovements[0];
      if (firstAction && firstAction.morphTargets && firstAction.morphTargets.length > 0) {
        const firstMorphTarget = this.getMorphTargetByName(firstAction.morphTargets[0].name);
        if (firstMorphTarget !== null) {
          return true;
        }
      }
    }

    // Fallback to legacy single mouth movement
    if (this.modelConfig.morphTargets.mouthMovement) {
      const mouthTarget = this.getMorphTargetByName(this.modelConfig.morphTargets.mouthMovement);
      return mouthTarget !== null;
    }

    return false;
  }

  // XXX: NOTE: When a .glb file is loaded, an animation group starts automatically.
  // Probably the first one on the list of animation groups.
  stopAllAnimationGroups() {
    for (const animationGroup of this.animationGroups) {
      animationGroup.stop();
    }
  }

  setAnim(animName: string, startPaused = false, onAnimationEnd?: () => void, playbackSpeed: number = 1.0) {
    if (!this.skeleton) {
      return;
    }

    // Safety check to prevent infinite recursion
    this.animationCallCount++;
    if (this.animationCallCount > this.MAX_ANIMATION_CALLS) {
      console.error(`Animation call limit exceeded (${this.MAX_ANIMATION_CALLS}). Stopping to prevent crash.`);
      this.animationCallCount = 0;
      return;
    }

    // Don't remember why I added this here :shrug:
    //this.disableSkeletonBlending();

    // This getAnimationRanges works for animation ranges - but not for animation groups.
    // And .glb files have animation groups.
    // console.log("skeleton animation names:", this.skeleton.getAnimationRanges());

    const animationGroup = this.scene.getAnimationGroupByName(animName);

    if (!animationGroup) {
      console.error("animationGroup is null ", animName);
      return;
    }

    // TODO: Uncomment this when we have a way to stop the default animations
    // this.stopAllAnimationGroups();

    // If there is a onAnimationEnd callback, then we DO NOT want to loop the current anim.
    animationGroup.start(!onAnimationEnd, playbackSpeed);

    // XXX: This onAnimationEnd logic was not tested.
    // (I also don't think it's being used anywhere)
    if (onAnimationEnd) {
      animationGroup.onAnimationEndObservable.addOnce(() => {
        if (onAnimationEnd) onAnimationEnd();
      });
    }

    if (startPaused) {
      animationGroup.pause();
    }

    this.currentAnimName = animName;
    this.currentAnimGroup = animationGroup;

    // The following enables blending when not using animation groups.
    // this.skeleton.enableBlending(0.05);
  }

  disableSkeletonBlending() {
    this.skeleton?.bones.forEach(function (bone) {
      bone.animations.forEach(function (animation) {
        animation.enableBlending = false;
      });
    });
  }

  getMorphTargetByName(morphTargetName: string | undefined): MorphTarget | null {
    if (!morphTargetName) {
      debugger;
      return null;
    }

    const morphTargetManager = this.faceMesh?.morphTargetManager;

    if (!morphTargetManager) {
      return null;
    }

    // Try the original name first
    let morphTargetIndex = getMorphTargetIndex(morphTargetManager, morphTargetName);

    // If not found and we have a face mesh name, try prepending it
    if (morphTargetIndex === -1 && this.modelConfig.faceMeshName) {
      const fullMorphTargetName = `${this.modelConfig.faceMeshName}.${morphTargetName}`;
      morphTargetIndex = getMorphTargetIndex(morphTargetManager, fullMorphTargetName);
    }

    if (morphTargetIndex === -1) {
      console.error(`getMorphTargetByName: Morph target not found: ${morphTargetName} (tried with and without face mesh prefix)`);
      return null;
    }

    const morphTarget = morphTargetManager.getTarget(morphTargetIndex);
    return morphTarget;
  }

  // New method to get a random mouth action based on probability
  getRandomMouthAction(): any {
    const mouthMovements = this.modelConfig.morphTargets.mouthMovements;
    if (!mouthMovements || mouthMovements.length === 0) {
      return null;
    }

    const randomValue = Math.random();
    let cumulativeProbability = 0;

    for (const action of mouthMovements) {
      cumulativeProbability += action.probability;
      if (randomValue <= cumulativeProbability) {
        return action;
      }
    }

    // Fallback to first action if no probability match
    return mouthMovements[0];
  }

  // New method to get a random eye action based on probability and cooldown
  getRandomEyeAction(): any {
    const eyeActions = this.modelConfig.morphTargets.eyeActions;
    if (!eyeActions || eyeActions.length === 0) {
      return null;
    }

    const currentTime = Date.now();
    const availableActions = eyeActions.filter(action => {
      // If no cooldown is defined, always allow the action
      if (action.cooldown === undefined) {
        return true;
      }

      const lastActionTime = this.eyeActionTimeouts.get(action.name) || 0;
      return (currentTime - lastActionTime) >= (action.cooldown * 1000);
    });

    if (availableActions.length === 0) {
      return null;
    }

    const randomValue = Math.random();
    let cumulativeProbability = 0;

    for (const action of availableActions) {
      cumulativeProbability += action.probability;
      if (randomValue <= cumulativeProbability) {
        return action;
      }
    }

    return availableActions[0];
  }

  // New method to play multiple morph target animations simultaneously
  playMorphTargetAction(action: any, onComplete?: () => void) {
    if (!action || !action.morphTargets || action.morphTargets.length === 0) {
      if (onComplete) onComplete();
      return;
    }

    let completedAnimations = 0;
    const totalAnimations = action.morphTargets.length;

    const checkCompletion = () => {
      completedAnimations++;
      if (completedAnimations >= totalAnimations && onComplete) {
        onComplete();
      }
    };

    action.morphTargets.forEach((morphAction: any, index: number) => {
      const morphTarget = this.getMorphTargetByName(morphAction.name);
      if (!morphTarget) {
        console.warn(`Morph target not found: ${morphAction.name}`);
        checkCompletion();
        return;
      }

      playMorphTargetAnim(
        `${action.name}_${morphAction.name}`,
        [0, morphAction.duration, morphAction.duration],
        [0, morphAction.weight, 0],
        morphTarget,
        checkCompletion,
        this.scene
      );
    });
  }

  // New method to perform eye actions
  performEyeAction() {
    // Only perform eye actions when talking
    if (!this.isTalking) {
      return;
    }

    if (this.isPerformingEyeAction) {
      return;
    }

    const eyeAction = this.getRandomEyeAction();
    if (!eyeAction) {
      return;
    }

    this.isPerformingEyeAction = true;
    this.eyeActionTimeouts.set(eyeAction.name, Date.now());

    // Clear any existing animations on the morph targets to prevent conflicts
    eyeAction.morphTargets.forEach((morphAction: any) => {
      const morphTarget = this.getMorphTargetByName(morphAction.name);
      if (morphTarget && morphTarget.animations) {
        // Stop any running animations
        this.scene.stopAnimation(morphTarget);
        // Clear animations array
        morphTarget.animations = [];
      }
    });

    this.playMorphTargetAction(eyeAction, () => {
      this.isPerformingEyeAction = false;
    });
  }



  setFaceMesh() {
    const faceMeshArray = this.meshes.filter((mesh) => {
      return mesh.name === this.modelConfig.faceMeshName;
    });

    const faceMesh = faceMeshArray[0] as Mesh;

    if (!faceMesh) {
      console.warn("Couldn't find face mesh :( no face morph targets will be available.");
      return;
    }

    this.faceMesh = faceMesh;
  }

  // New method to validate morph target configurations
  validateMorphTargetConfig() {
    if (!this.faceMesh?.morphTargetManager) {
      console.warn("No morph target manager available for validation");
      return;
    }

    const morphTargetManager = this.faceMesh.morphTargetManager;
    const availableTargets = getAvailableMorphTargets(morphTargetManager);

    // Validate mouth movements
    if (this.modelConfig.morphTargets.mouthMovements) {
      this.modelConfig.morphTargets.mouthMovements.forEach((action, index) => {
        const validation = validateMorphTargetAction(morphTargetManager, action);
        if (!validation.valid) {
          console.warn(`Mouth action "${action.name}" (index ${index}) has missing targets:`, validation.missingTargets);
        }
      });
    }

    // Validate eye actions
    if (this.modelConfig.morphTargets.eyeActions) {
      this.modelConfig.morphTargets.eyeActions.forEach((action, index) => {
        const validation = validateMorphTargetAction(morphTargetManager, action);
        if (!validation.valid) {
          console.warn(`Eye action "${action.name}" (index ${index}) has missing targets:`, validation.missingTargets);
        }
      });
    }

    // Validate legacy mouth movement
    if (this.modelConfig.morphTargets.mouthMovement) {
      const mouthTarget = this.getMorphTargetByName(this.modelConfig.morphTargets.mouthMovement);
      if (!mouthTarget) {
        console.warn(`Legacy mouth movement target "${this.modelConfig.morphTargets.mouthMovement}" not found`);
      }
    }
  }

  blink() {
    if (this.isBlinkingLeftEye || this.isBlinkingRightEye) {
      return;
    }

    const blinkOpenTimeSeconds = 0.15;
    const blinkCloseTimeSeconds = 0.2;

    const leftEyeCloseTarget = this.getMorphTargetByName(
      this.modelConfig.morphTargets.leftEyeClose
    );
    const rightEyeCloseTarget = this.getMorphTargetByName(
      this.modelConfig.morphTargets.rightEyeClose
    );

    if (!leftEyeCloseTarget || !rightEyeCloseTarget) {
      return;
    }

    this.isBlinkingRightEye = true;
    this.isBlinkingLeftEye = true;

    const blinkEndCallback = (isLeftEye: boolean) => {
      if (isLeftEye) {
        this.isBlinkingLeftEye = false;
      } else {
        this.isBlinkingRightEye = false;
      }

      if (this.isBlinkingLeftEye || this.isBlinkingRightEye) {
        return;
      }

      this.blinkTimeoutId = window.setTimeout(() => this.blink(), random(200, 800));
    };

    playMorphTargetAnim(
      "leftEyeBlink",
      [0, blinkCloseTimeSeconds, blinkOpenTimeSeconds],
      [0, 1, 0],
      leftEyeCloseTarget,
      () => blinkEndCallback(true),
      this.scene
    );
    playMorphTargetAnim(
      "rightEyeBlink",
      [0, blinkCloseTimeSeconds, blinkOpenTimeSeconds],
      [0, 1, 0],
      rightEyeCloseTarget,
      () => blinkEndCallback(false),
      this.scene
    );
  }

  getRandomIdleAnim(): { name: string; playbackSpeed: number } {
    const sampledIdleAnim = sample(this.modelConfig.idleAnimations)

    if (!sampledIdleAnim) {
      throw new Error("Failed to get sampledIdleAnim :(");
    }

    // Handle both string and object formats
    if (typeof sampledIdleAnim === 'string') {
      return { name: sampledIdleAnim, playbackSpeed: 1.0 };
    } else {
      return {
        name: sampledIdleAnim.name,
        playbackSpeed: sampledIdleAnim.playbackSpeed || 1.0
      };
    }
  }

  getRandomTalkingAnim(): { name: string; playbackSpeed: number } {
    const sampledTalkingAnim = sample(this.modelConfig.talkingBodyAnimations)

    if (!sampledTalkingAnim) {
      throw new Error("Failed to get sampledTalkingAnim :(");
    }

    // Handle both string and object formats
    if (typeof sampledTalkingAnim === 'string') {
      return { name: sampledTalkingAnim, playbackSpeed: 1.0 };
    } else {
      return {
        name: sampledTalkingAnim.name,
        playbackSpeed: sampledTalkingAnim.playbackSpeed || 1.0
      };
    }
  }

  talkAnimationEnd(info: string, playRandomIdleAnim = true) {
    // console.log("talkAnimationEnd - this.isTalking", this.isTalking, info);

    if (!this.isTalking) {
      return;
    }

    this.isTalking = false;
    this.currentMouthAction = null;

    // Record the time when talking ended for cooldown
    this.lastTalkingEndTime = Date.now();

    // Stop eye actions when talking ends
    this.stopEyeActions();

    if (playRandomIdleAnim) {
      // Add cooldown delay before transitioning to idle
      setTimeout(() => {
        // Check if we're still not talking (no new talking started)
        if (!this.isTalking) {
          const idleAnim = this.getRandomIdleAnim();
          this.setAnim(idleAnim.name, false, undefined, idleAnim.playbackSpeed);
        }
      }, this.TALKING_END_COOLDOWN);
    }
  }

  talkAnimationStart() {
    if (this.isTalking) {
      return;
    }

    //
    ////
    ////// Play talking facial animation.

    const maxTime = 1.5;
    const minTime = 0.2;

    // Try to use new multiple mouth movements first
    const mouthAction = this.getRandomMouthAction();

    if (mouthAction) {
      // Use new multiple mouth movements system
      this.currentMouthAction = mouthAction.name;

      const mouthAnimEnd = () => {
        if (!this.isTalking) {
          return;
        }

        // Get a new random mouth action for variety
        const nextMouthAction = this.getRandomMouthAction();
        if (nextMouthAction) {
          this.currentMouthAction = nextMouthAction.name;
          this.playMorphTargetAction(nextMouthAction, mouthAnimEnd);
        }
      };

      this.playMorphTargetAction(mouthAction, mouthAnimEnd);
    } else {
      // Fallback to legacy single mouth movement
      const mouthOpenTarget = this.getMorphTargetByName(this.modelConfig.morphTargets.mouthMovement);

      if (!mouthOpenTarget) {
        console.error("talkAnimationStart: No mouth target found, returning");
        return;
      }

      // Recursive thingy - when the animation ends, it calls itself again.
      const vowelAnimEnd = () => {
        if (!this.isTalking) {
          return;
        }

        playMorphTargetAnim(
          "aSoundAnim",
          [0, random(minTime, maxTime), random(minTime, maxTime)],
          [0, 1, 0],
          mouthOpenTarget,
          vowelAnimEnd,
          this.scene
        );
      };

      playMorphTargetAnim(
        "aSoundAnim",
        [0, random(minTime, maxTime), random(minTime, maxTime)],
        [0, 1, 0],
        mouthOpenTarget,
        vowelAnimEnd,
        this.scene
      );
    }

    this.isTalking = true;

    // Start eye actions for more natural behavior
    this.startEyeActions();

    //
    ////
    ////// Play talking body animation.

    const alternateBetweenAnims = (currentAnim: { name: string; playbackSpeed: number }, nextAnim: string) => {
      if (!this.isTalking) {
        return;
      }

      // Use the configured playback speed for talking animations
      this.setAnim(currentAnim.name, false, () => {
        // Get next random talking animation
        const nextTalkingAnim = this.getRandomTalkingAnim();
        alternateBetweenAnims(nextTalkingAnim, currentAnim.name);
      }, currentAnim.playbackSpeed);
    };

    const firstTalkingAnim = this.getRandomTalkingAnim();
    alternateBetweenAnims(firstTalkingAnim, this.currentAnimName);
  }

  // New method to start eye actions during talking
  startEyeActions() {
    if (!this.modelConfig.morphTargets.eyeActions || this.modelConfig.morphTargets.eyeActions.length === 0) {
      return;
    }

    // Clear any existing eye action timeout
    if (this.eyeActionTimeoutId !== null) {
      clearTimeout(this.eyeActionTimeoutId);
      this.eyeActionTimeoutId = null;
    }

    const performRandomEyeAction = () => {
      if (!this.isTalking) {
        return;
      }

      this.performEyeAction();

      // Schedule next eye action
      const nextEyeActionDelay = random(200, 600); // 200-600ms
      this.eyeActionTimeoutId = setTimeout(performRandomEyeAction, nextEyeActionDelay);
    };

    // Start the first eye action after a short delay
    const initialDelay = random(100, 300);
    this.eyeActionTimeoutId = setTimeout(performRandomEyeAction, initialDelay);
  }

  // New method to stop eye actions
  stopEyeActions() {
    this.isPerformingEyeAction = false;

    // Clear any ongoing eye action timeouts
    this.eyeActionTimeouts.clear();

    // Clear eye action timeout
    if (this.eyeActionTimeoutId !== null) {
      clearTimeout(this.eyeActionTimeoutId);
      this.eyeActionTimeoutId = null;
    }

    // Stop any ongoing morph target animations for eye actions
    if (this.modelConfig.morphTargets.eyeActions) {
      this.modelConfig.morphTargets.eyeActions.forEach((action) => {
        action.morphTargets.forEach((morphAction) => {
          const morphTarget = this.getMorphTargetByName(morphAction.name);
          if (morphTarget && morphTarget.animations) {
            this.scene.stopAnimation(morphTarget);
            morphTarget.animations = [];
          }
        });
      });
    }
  }



  // New method to set up proper idle behavior
  setupIdleBehavior() {
    // Stop any running animations first
    // TODO: Uncomment this when we have a way to stop the default animations
    // this.stopAllAnimationGroups();

    // Stop any eye actions when going to idle
    this.stopEyeActions();

    // Check if we're in cooldown period after talking ended
    if (this.isInTalkingEndCooldown()) {
      // Still in cooldown, delay the idle setup
      const remainingCooldown = this.TALKING_END_COOLDOWN - (Date.now() - this.lastTalkingEndTime);
      setTimeout(() => {
        this.setupIdleBehavior();
      }, remainingCooldown);
      return;
    }

    // If no initial animation is configured or it's empty, start with a random idle animation
    if (!this.modelConfig.initialAnimation || this.modelConfig.initialAnimation.trim() === '') {
      const idleAnim = this.getRandomIdleAnim();
      this.setAnim(idleAnim.name, false, undefined, idleAnim.playbackSpeed);
    }

    // Set up periodic idle animation changes for variety
    this.setupIdleAnimationCycling();
  }

  // New method to cycle through idle animations for variety
  setupIdleAnimationCycling() {
    if (this.modelConfig.idleAnimations.length <= 1) {
      return;
    }

    // Clear any existing idle cycle timeout
    if (this.idleCycleTimeoutId !== null) {
      clearTimeout(this.idleCycleTimeoutId);
      this.idleCycleTimeoutId = null;
    }

    const cycleIdleAnimations = () => {
      if (this.isTalking) {
        return;
      }

      // Check if we're still in cooldown period after talking ended
      if (this.isInTalkingEndCooldown()) {
        // Still in cooldown, schedule next check
        const remainingCooldown = this.TALKING_END_COOLDOWN - (Date.now() - this.lastTalkingEndTime);
        this.idleCycleTimeoutId = setTimeout(cycleIdleAnimations, remainingCooldown);
        return;
      }

      // Reset animation call counter periodically
      this.resetAnimationCallCounter();

      const idleAnim = this.getRandomIdleAnim();
      this.setAnim(idleAnim.name, false, undefined, idleAnim.playbackSpeed);

      // Schedule next cycle (between 10-30 seconds)
      const nextCycleDelay = Math.random() * 20000 + 10000; // 10-30 seconds
      this.idleCycleTimeoutId = setTimeout(cycleIdleAnimations, nextCycleDelay);
    };

    // Start the first cycle after a delay
    const initialDelay = Math.random() * 10000 + 5000; // 5-15 seconds
    this.idleCycleTimeoutId = setTimeout(cycleIdleAnimations, initialDelay);
  }

  // Method to check if we're in cooldown period after talking ended
  private isInTalkingEndCooldown(): boolean {
    const timeSinceTalkingEnd = Date.now() - this.lastTalkingEndTime;
    return timeSinceTalkingEnd < this.TALKING_END_COOLDOWN;
  }

  // Method to reset animation call counter (called periodically to prevent false positives)
  resetAnimationCallCounter() {
    this.animationCallCount = 0;
  }

  // Method to get current animation state for debugging
  getCurrentAnimationState() {
    return {
      isTalking: this.isTalking,
      currentAnimName: this.currentAnimName,
      currentAnimGroup: this.currentAnimGroup?.name || 'none',
      isPerformingEyeAction: this.isPerformingEyeAction,
      currentMouthAction: this.currentMouthAction
    };
  }

  dispose() {
    this.scene.stopAllAnimations();

    // Remove all animation groups:
    // https://forum.babylonjs.com/t/proper-way-to-disconnect-and-reconnect-an-animation-group-in-scene/38878/9
    this.scene.animationGroups = [];

    // Reset animation call counter
    this.animationCallCount = 0;

    // Reset cooldown tracking
    this.lastTalkingEndTime = 0;

    // Clear all timeouts
    if (this.blinkTimeoutId !== null) {
      window.clearTimeout(this.blinkTimeoutId);
      this.blinkTimeoutId = null;
    }

    if (this.idleCycleTimeoutId !== null) {
      clearTimeout(this.idleCycleTimeoutId);
      this.idleCycleTimeoutId = null;
    }

    if (this.eyeActionTimeoutId !== null) {
      clearTimeout(this.eyeActionTimeoutId);
      this.eyeActionTimeoutId = null;
    }

    // Clear eye action timeouts
    this.eyeActionTimeouts.clear();

    // Stop any ongoing morph target animations
    if (this.faceMesh?.morphTargetManager) {
      const morphTargetManager = this.faceMesh.morphTargetManager;
      for (let i = 0; i < morphTargetManager.numTargets; i++) {
        const morphTarget = morphTargetManager.getTarget(i);
        if (morphTarget && morphTarget.animations) {
          this.scene.stopAnimation(morphTarget);
          morphTarget.animations = [];
        }
      }
    }

    // Dispose of meshes
    this.meshes.forEach(mesh => {
      if (mesh) {
        mesh.dispose();
      }
    });

    this.mainMesh?.dispose();
  }

  DEBUGSTUFF() {
    if (this.skeletonViewer == null) {
      if (this.skeleton == null) {
        console.error("skeleton is null :(");
        return;
      }

      if (this.mainMesh == null) {
        console.error("mainMesh is null :(");
        return;
      }

      // 1st call:

      // DEBUG
      this.scene.registerAfterRender(() => {
        this.DEBUGSTUFF();
      });

      // var dummyBox = new MeshBuilder.CreateBox("dummyBox", { size: 0.05 }, this.scene)
      // var CoTAxis = localAxes(1, 0); CoTAxis.parent = dummyBox

      this.skeletonViewer = new Debug.SkeletonViewer(this.skeleton, this.mainMesh, this.scene);
      this.skeletonViewer.isEnabled = true;
      this.skeletonViewer.color = Color3.White();
    } else {
      // SUBSEQUENT CALLS:
      this.skeletonViewer.update();

      //this.updateEllipsoidMesh();

      // let dragMesh = this.scene.getMeshByName(this.skeleton.name + "_dragMesh");
      // this.trackingBone = this.skeleton.bones[this.skeleton.getBoneIndexByName("Hips")];

      // if (this.trackingBone != null && dragMesh != null) {
      //     //dragMesh.position = this.trackingBone.getAbsolutePosition(this.mainMesh);
      // } else if (this.trackingBone != null && dragMesh == null) {
      //     this.draggable();
      // }
    }
  }
}
